import React, { useState, useMemo } from 'react';
import {
    KatLayout,
    KatProgress,
} from '@amzn/katal-react';

// Core hooks
import {
    getGSLADataForWarehouseAsMulti,
    getGSLARecommendationsForWarehouse,
    isRecommendationFresh,
    useFCMetadata,
} from 'src/core/hooks/useGranularSLA';
import { useURLSiteParam } from 'src/core/hooks/UseURLSiteParam';
import { useUser } from 'src/utils/UserContext';
import { useIsDefaultFCPCEnabled } from 'src/core/hooks/useIsDefaultFCPCEnabled';
import { usePCForWarehouse } from 'src/core/hooks/usePCForWarehouse';
import { getValidWHTsForCountry } from 'src/types/WarehouseHandlingTier';

// Reuse the existing hierarchical table adapter
import { GSLAHierarchicalTableAdapter } from '../../components/hierarchicalTable/GSLAHierarchicalTableAdapter';

// Types
import { GSLARecommendationsByMutipleWarehouses } from 'src/features/types/gslaApi';

// Styles
import '../../styles/granularSLA/productionCapability.scss';

/**
 * ProductionCapabilityManagerWithHierarchy replaces the original ProductionCapabilityManager
 * with a new implementation that uses the hierarchical table to display GSLA data
 * including handling tiers.
 */
const ProductionCapabilityManagerWithHierarchy = () => {
    // User context for permissions
    const { user } = useUser();

    // Check if user has edit permissions
    const hasEditPermission = user?.isAuthorizedForSLAOverrides === true || user?.isDevAdmin === true;

    // State for view-only mode - default to false for users with permissions
    const [viewOnlyMode, setViewOnlyMode] = useState<boolean>(false);

    // State for success notification
    const [showSuccessNotification, setShowSuccessNotification] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    // Get the site ID from URL
    const siteId = useURLSiteParam();

    // PSSD detection for workflow guidance
    const { isDefaultFCPCEnabled } = useIsDefaultFCPCEnabled(siteId || '');
    const isPSSD = !isDefaultFCPCEnabled;

    // Fetch FC metadata for country-based WHT filtering
    const {
        data: fcMetadata,
        isLoading: isFCMetadataLoading,
    } = useFCMetadata(siteId || '');

    // Compute available tiers based on country code
    const availableTiers = getValidWHTsForCountry(fcMetadata?.countryCode, siteId);

    // Fetch all CDS PCs for this warehouse (for unconfigured PC detection on PSSD sites)
    const { availablePCs: cdsPCs, isLoading: isCDSLoading } = usePCForWarehouse(
        siteId ? [siteId] : [],
        isPSSD,
    );

    // State for dismissing the unconfigured PCs alert
    const [isUnconfiguredAlertDismissed, setIsUnconfiguredAlertDismissed] = useState(false);

    // Fetch GSLA data using the single-warehouse endpoint (has PC expansion logic)
    // Returns data in multi-warehouse format for GSLAHierarchicalTableAdapter
    const {
        data: gslaData,
        isLoading: isGSLADataLoading,
        refetch: refetchGSLA,
    } = getGSLADataForWarehouseAsMulti(siteId || '', !viewOnlyMode);

    // Fetch GSLA recommendations (single-warehouse, wrapped to multi-warehouse format)
    const {
        data: gslaRecommendationsRaw,
        isLoading: isGSLARecommendationsLoading,
    } = getGSLARecommendationsForWarehouse(
        siteId || '',
        { enabled: !!(user?.isAuthorizedForSLAOverrides || user?.isDevAdmin) },
    );

    // Wrap single-warehouse recommendations into multi-warehouse format for the adapter
    // Filter out stale recommendations (consistent with adaptGSLARecommendationsResponse)
    const gslaRecommendations: GSLARecommendationsByMutipleWarehouses | undefined =
        gslaRecommendationsRaw && siteId
            ? {
                warehouseToGSLARecommendationMap: {
                    [siteId]: gslaRecommendationsRaw.pcToGslaList.filter(
                        rec => isRecommendationFresh(rec.publishDateEpochMillis),
                    ),
                },
            }
            : undefined;

    // PSSD: Detect CDS PCs that have no GSLA entry for this site
    const GSLA_EXCLUDED_PCS = new Set(['Default_Network_PC', 'Default_FC_PC', 'STANDARD_PC', 'Default_PC']);
    const unconfiguredPCs = useMemo(() => {
        if (!isPSSD || !gslaData || isCDSLoading || cdsPCs.length === 0) {
            return [];
        }

        // Collect all PCs that have GSLA data
        const configuredPCs = new Set<string>();
        const allGSLAs = [
            ...(gslaData.warehouseToGSLAMap[siteId || ''] || []),
            ...(gslaData.warehouseToGSLAMap['DEFAULT'] || []),
        ];
        allGSLAs.forEach(def => configuredPCs.add(def.productionCapability));

        // Find CDS PCs that are not configured in GSLA (excluding system PCs)
        return cdsPCs.filter(pc => !configuredPCs.has(pc) && !GSLA_EXCLUDED_PCS.has(pc));
    }, [isPSSD, gslaData, cdsPCs, isCDSLoading, siteId]);

    // Handle successful save
    const handleSaveSuccess = (message?: string) => {
        // Refetch data to ensure we have the latest values
        refetchGSLA();

        // Show success notification
        setSuccessMessage(message || 'GSLA values published successfully!');
        setShowSuccessNotification(true);

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
            setShowSuccessNotification(false);
        }, 5000);
    };

    // Function to toggle view-only mode
    const toggleViewMode = () => {
        setViewOnlyMode(prev => !prev);
    };

    // Determine if user can edit based on permissions and view mode
    const userCanEdit = hasEditPermission && !viewOnlyMode;

    // Show loading state
    if (isGSLADataLoading || isGSLARecommendationsLoading || isFCMetadataLoading) {
        return (
            <div>
                GSLA Data is loading please wait a few seconds. The page will automatically load when it is ready.
                <KatProgress indeterminate></KatProgress>
            </div>
        );
    }

    // Main view for all users
    return (
        <React.Fragment>
            {/* Full-screen alert for unconfigured CDS PCs on PSSD sites */}
            {isPSSD && unconfiguredPCs.length > 0 && !isUnconfiguredAlertDismissed && (
                <div className="pssd-fullscreen-alert-overlay">
                    <div className="pssd-fullscreen-alert">
                        <h2>⚠️ Action Required — Missing GSLA Configuration</h2>
                        <p>
                            This is a PSSD site. The following {unconfiguredPCs.length} Production
                            {unconfiguredPCs.length === 1 ? ' Capability does' : ' Capabilities do'} not
                            have GSLA (Granular SLA) values configured for <strong>{siteId}</strong>.
                        </p>
                        <p>
                            Without GSLA values, TRB thresholds cannot be set for these Production Capabilities.
                            Please configure GSLA entries below to enable full site operation.
                        </p>
                        <ul>
                            {unconfiguredPCs.map(pc => (
                                <li key={pc}>{pc}</li>
                            ))}
                        </ul>
                        <button
                            className="pssd-fullscreen-alert-dismiss"
                            onClick={() => setIsUnconfiguredAlertDismissed(true)}
                        >
                            Continue to configure GSLA
                        </button>
                    </div>
                </div>
            )}
            <KatLayout>
                <div slot='main'>
                    <h3>Granular SLA Data for: {siteId}</h3>

                    {/* PSSD workflow guidance */}
                    {isPSSD && (
                        <div className="pssd-workflow-banner warning">
                            ⚠️ This is a PSSD site. After configuring GSLA values, set TRB thresholds on the <a href={`/r2f-threshold-config/${siteId}`}>TRB page →</a>
                        </div>
                    )}


                    {/* Success Notification */}
                    {showSuccessNotification && (
                        <div style={{
                            marginBottom: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#d4edda',
                            border: '1px solid #c3e6cb',
                            borderRadius: '4px',
                            color: '#155724',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <span style={{ fontWeight: 500 }}>{successMessage}</span>
                            <button
                                onClick={() => setShowSuccessNotification(false)}
                                style={{
                                    marginLeft: '16px',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    color: '#155724',
                                    padding: '0 4px',
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    {/* Only show toggle if user has edit permissions */}
                    {hasEditPermission && (
                        <div style={{
                            padding: '16px',
                            backgroundColor: '#f5f5f5',
                            borderBottom: '1px solid #ddd',
                            marginBottom: '16px',
                        }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={viewOnlyMode}
                                    onChange={toggleViewMode}
                                    style={{ marginRight: '8px' }}
                                />
                                <span style={{ fontWeight: 500 }}>View-Only Mode</span>
                            </label>
                        </div>
                    )}

                    <GSLAHierarchicalTableAdapter
                        warehouseId={siteId || ''}
                        gslaData={gslaData}
                        gslaRecommendations={gslaRecommendations}
                        isLoading={isGSLADataLoading || isGSLARecommendationsLoading}
                        userCanEdit={userCanEdit}
                        onSaveSuccess={handleSaveSuccess}
                        availableTiers={availableTiers}
                    />
                </div>
            </KatLayout>
        </React.Fragment>
    );
};

export default ProductionCapabilityManagerWithHierarchy;
